import { Dispatch, SetStateAction, useRef } from "react";
import { useTranslation } from "react-i18next";
import MaterialTable, { Column } from "@material-table/core";

import { Typography } from "@mui/material";
import { ProgramRole, TraineeType, User } from "../../API";
import {
  BulkInviteInvitationStatus,
  BulkInvitation,
  bulkInviteTableRowUpdate,
  bulkInviteTableRowDelete,
  bulkInviteRowRender,
  bulkInviteRowHasError,
  bulkInviteGetFormattedErrors,
  bulkInviteRowContainsExistingUserError,
  getBulkInviteIdentifierConfigErrors,
  bulkExtraIdentifiers
} from "./userInvitationUtils";
import { ProgramIdentifierConfig } from "../Networks/networksAPI";
import { identifierKeyForColumn } from "../Networks/identifierUtils";
import { Role } from "../Auth/roles";
import { TrainingStage } from "../TrainingStages/trainingStagesAPI";
import { ExistingUserLookupErrors, Regex } from "../../app/constants";
import { cloneDeep } from "lodash";
import { useAppDispatch } from "../../app/hooks";
import { setModalContent, setShowModal } from "../../common-ui/modalSlice";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import { AddUserToProgram } from "../Users/AddUserToProgram";

interface UserInvitationsBulkInviteTableProps {
  validatedInvitations: BulkInvitation[];
  currentInvitations: BulkInvitation[];
  setInvitations: Dispatch<SetStateAction<BulkInvitation[] | undefined>>;
  loading?: boolean;
  currentUserRoles?: Role[];
  hasCustomProgramId?: boolean;
  // Whether the uploaded CSV included each column; drives column visibility so
  // users only see (and can only fill) the column their template actually uses.
  csvHasPgyColumn?: boolean;
  csvHasYearInStageColumn?: boolean;
  // When true, keep BOTH pgy and yearInStage columns visible regardless of the
  // CSV-presence hiding (any involved program is Fellowship or unresolved).
  forceBothYearAndPgyColumns?: boolean;
  // Resolves a program's training stage so the pgy/year validators can be
  // stage-aware (Fellowship requires BOTH; other stages require either) and can
  // enforce the stage's min/max range live as the user types.
  getStageForProgramId?: (programId?: string) => TrainingStage | undefined;
  statusMessages: Map<string, BulkInviteInvitationStatus | string>;
  setStatusMessages: Dispatch<SetStateAction<Map<string, BulkInviteInvitationStatus | string>>>;
  getUsersByEmail: (email: string) => Promise<User[] | null | undefined>;
  getUsersByNPI: (npi: number) => Promise<User[] | null | undefined>;
  selectedInstitutionId?: string;
  selectedProgramId?: string;
  // Per-program identifier config (keyed by programId) for config-driven inline
  // identifier validation. Empty/absent for a program → no identifier rules.
  identifierConfigsByProgram?: Record<string, ProgramIdentifierConfig>;
  // Config-authoritative column visibility: show an identifier column only when
  // some involved network collects it (SysAdmin sees all). Default true.
  showNpiColumn?: boolean;
  showLearnColumn?: boolean;
  // SE-3118: other identifiers (beyond NPI / LEARN), one column each, keyed by
  // their CSV column (e.g. { field: "aamcId", title: "AAMC ID" }).
  extraIdentifierColumns?: { field: string; title: string }[];
  onRowDelete?: (
    updatedInvitations: BulkInvitation[],
    deletedInvitation?: BulkInvitation,
    options?: { skipValidation?: boolean }
  ) => void;
  rowsAwaitingRevalidation: Set<string>;
  setRowsAwaitingRevalidation: Dispatch<SetStateAction<Set<string>>>;
}

export const UserInvitationsBulkInviteTable = ({
  setInvitations,
  loading,
  currentUserRoles,
  hasCustomProgramId,
  csvHasPgyColumn,
  csvHasYearInStageColumn,
  forceBothYearAndPgyColumns,
  getStageForProgramId,
  statusMessages,
  setStatusMessages,
  getUsersByEmail,
  getUsersByNPI,
  selectedInstitutionId,
  selectedProgramId,
  identifierConfigsByProgram,
  showNpiColumn = true,
  showLearnColumn = true,
  extraIdentifierColumns = [],
  validatedInvitations,
  currentInvitations,
  onRowDelete,
  rowsAwaitingRevalidation,
  setRowsAwaitingRevalidation
}: UserInvitationsBulkInviteTableProps) => {
  const editableInvitations = cloneDeep(currentInvitations);
  const tableRef = useRef<MaterialTable<BulkInvitation>>(null);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  // Config-driven identifier errors for a row (required/regex per the row's
  // program + role), mirroring the backend. Empty when config isn't loaded or a
  // network declares no identifier rules — so it never invents a false error.
  const identifierConfigErrorsForRow = (
    row: BulkInvitation
  ): Record<string, string> =>
    getBulkInviteIdentifierConfigErrors(
      row,
      identifierConfigsByProgram ?? {},
      selectedProgramId
    );

  const columns: Array<
    Column<BulkInvitation & { tableData?: { editing: string } }>
  > = [
    {
      title: t("First Name"),
      field: "firstName",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "firstName",
            editableInvitations,
            currentInvitations
          ) &&
            row.firstName.length > 0 &&
            row.firstName.length <= 40),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "firstName",
          "Required, 1-40 chars.",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Last Name"),
      field: "lastName",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "lastName",
            editableInvitations,
            currentInvitations
          ) &&
            row.lastName.length > 0 &&
            row.lastName.length <= 40),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "lastName",
          "Required, 1-40 chars.",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Email"),
      field: "email",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "email",
            editableInvitations,
            currentInvitations
          ) &&
            row.email.length !== 0 &&
            Regex.Email.test(row.email)),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "email",
          "Required, valid email.",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("NPI"),
      field: "npi",
      hidden: !showNpiColumn,
      validate: (row) => {
        // Required-ness comes from the network's identifier config (config-driven,
        // per role), computed inline here so feedback is immediate and correct for
        // any network — not hardcoded by role. Format is checked when a value is
        // present. The backend re-enforces the same rules at send time.
        const npiVal = row.npi;
        const formatOk =
          !npiVal || (npiVal >= 1000000000 && npiVal <= 1999999999);
        const configError = identifierConfigErrorsForRow(row)["npi"];
        return {
          isValid:
            row.tableData?.editing === "delete" ||
            (!bulkInviteRowHasError(
              row,
              "npi",
              editableInvitations,
              currentInvitations
            ) &&
              formatOk &&
              !configError),
          helperText:
            configError ||
            bulkInviteGetFormattedErrors(
              row,
              "npi",
              "If provided: a 10-digit number starting with 1",
              editableInvitations,
              currentInvitations
            )
        };
      }
    },
    {
      title: t("SIMPL LearnID"),
      field: "simplLearnId",
      hidden: !showLearnColumn,
      cellStyle: {
        maxWidth: "150px",
        overflow: "hidden",
        textOverflow: "ellipsis"
      },
      // Required-ness/validity comes from the network's identifier config,
      // computed inline (config-driven, per role) so feedback is immediate and
      // correct for any network. The backend re-enforces the same rules at send
      // time. No hardcoded client-side rule.
      validate: (row) => {
        const configError = identifierConfigErrorsForRow(row)["simplLearnId"];
        return {
          isValid:
            row.tableData?.editing === "delete" ||
            (!bulkInviteRowHasError(
              row,
              "simplLearnId",
              editableInvitations,
              currentInvitations
            ) &&
              !configError),
          helperText:
            configError ||
            bulkInviteGetFormattedErrors(
              row,
              "simplLearnId",
              "",
              editableInvitations,
              currentInvitations
            )
        };
      }
    },
    // Config-driven columns for any other identifier: required/format come from
    // the network config inline; existing / repeated values come back from the
    // backend validator on the same field.
    ...extraIdentifierColumns.map(
      ({ field, title }): Column<BulkInvitation & { tableData?: { editing: string } }> => ({
        title: t(title),
        field: field as keyof BulkInvitation,
        validate: (row) => {
          const configError = identifierConfigErrorsForRow(row)[field];
          return {
            isValid:
              row.tableData?.editing === "delete" ||
              (!bulkInviteRowHasError(
                row,
                field,
                editableInvitations,
                currentInvitations
              ) &&
                !configError),
            helperText:
              configError ||
              bulkInviteGetFormattedErrors(
                row,
                field,
                "",
                editableInvitations,
                currentInvitations
              )
          };
        }
      })
    ),
    {
      title: t("Clinical Training Year in Program"),
      field: "yearInStage",
      // Hide when every involved program is non-fellowship and the CSV used PGY
      // but not this column. Shown for fellowship/unresolved programs, or when
      // both/neither column are present.
      hidden:
        !!csvHasPgyColumn &&
        !csvHasYearInStageColumn &&
        !forceBothYearAndPgyColumns,
      validate: (row) => {
        // Stage-aware: Fellowship requires BOTH pgy and yearInStage independently;
        // other stages require either one. (Range is enforced by the server.)
        const isTrainee = row.role === ProgramRole.Trainee;
        const stage = getStageForProgramId?.(row.programId || selectedProgramId);
        const isFellowship = stage?.code === "FELLOWSHIP";
        // yearInStage is always bound by the stage's own min/max.
        const yearMin = stage?.minYear ?? 1;
        const yearMax = stage?.maxYear;
        const rawYear = row.yearInStage as unknown;
        const yearNum =
          rawYear == null || rawYear === "" ? null : Number(rawYear);
        const yearValid =
          yearNum != null &&
          Number.isInteger(yearNum) &&
          yearNum >= yearMin &&
          (yearMax == null || yearNum <= yearMax);
        const yearIsEmpty = rawYear == null || rawYear === "";
        const rawPgy = (row as any).pgy as unknown;
        const pgyProvided = rawPgy != null && rawPgy !== "";
        const rangeText =
          yearMax != null ? `${yearMin}–${yearMax}` : "positive integer";
        return {
          isValid:
            row.tableData?.editing === "delete" ||
            (!bulkInviteRowHasError(
              row,
              "yearInStage",
              editableInvitations,
              currentInvitations
            ) &&
              (isTrainee
                ? isFellowship
                  ? yearValid
                  : yearValid || (yearIsEmpty && pgyProvided)
                : yearIsEmpty)),
          helperText: bulkInviteGetFormattedErrors(
            row,
            "yearInStage",
            isTrainee
              ? isFellowship
                ? `Required for fellowship trainees; ${rangeText}`
                : `Required for trainees (or provide PGY); ${rangeText}`
              : "(do not use for this role)",
            editableInvitations,
            currentInvitations
          )
        };
      }
    },
    {
      title: t("PGY"),
      field: "pgy",
      // Hide when every involved program is non-fellowship and the CSV used the
      // year column but not PGY. Shown for fellowship/unresolved programs, or
      // when both/neither column are present.
      hidden:
        !!csvHasYearInStageColumn &&
        !csvHasPgyColumn &&
        !forceBothYearAndPgyColumns,
      validate: (row) => {
        // Stage-aware: Fellowship requires BOTH pgy and yearInStage independently;
        // other stages require either one. (Range is enforced by the server.)
        const isTrainee = row.role === ProgramRole.Trainee;
        const stage = getStageForProgramId?.(row.programId || selectedProgramId);
        const isFellowship = stage?.code === "FELLOWSHIP";
        // Fellowship PGY is independent (1–12); for other stages pgy ≡ yearInStage,
        // so it's bound by the same stage min/max.
        const pgyMin = isFellowship || stage?.minYear == null ? 1 : stage.minYear;
        const pgyMax = isFellowship || stage?.maxYear == null ? 12 : stage.maxYear;
        const rawPgy = (row as any).pgy as unknown;
        const pgyIsEmpty = rawPgy == null || rawPgy === "";
        const pgyNum = pgyIsEmpty ? null : Number(rawPgy);
        const pgyInRange =
          pgyNum != null &&
          Number.isInteger(pgyNum) &&
          pgyNum >= pgyMin &&
          pgyNum <= pgyMax;
        const rawYear = row.yearInStage as unknown;
        const yearProvided = rawYear != null && rawYear !== "";
        const rangeText = `${pgyMin}–${pgyMax}`;
        return {
          isValid:
            row.tableData?.editing === "delete" ||
            (!bulkInviteRowHasError(
              row,
              "pgy",
              editableInvitations,
              currentInvitations
            ) &&
              (isTrainee
                ? isFellowship
                  ? pgyInRange
                  : pgyInRange || (pgyIsEmpty && yearProvided)
                : pgyIsEmpty)),
          helperText: bulkInviteGetFormattedErrors(
            row,
            "pgy",
            isTrainee
              ? isFellowship
                ? `Required for fellowship trainees; whole number ${rangeText}`
                : `Required for trainees (or provide Clinical Training Year); whole number ${rangeText}`
              : "(do not use for this role)",
            editableInvitations,
            currentInvitations
          )
        };
      }
    },
    {
      title: t("Program ID (optional)"),
      field: "programId",
      hidden:
        !hasCustomProgramId &&
        !currentUserRoles?.includes(Role.SystemAdmin),
      validate: (row) => {
        return {
          isValid:
            row.tableData?.editing === "delete" ||
            !bulkInviteRowHasError(
              row,
              "programId",
              editableInvitations,
              currentInvitations
            ),
          helperText: bulkInviteGetFormattedErrors(
            row,
            "programId",
            "Overwrites the program selected above.",
            editableInvitations,
            currentInvitations
          )
        };
      }
    },
    {
      title: t("Role"),
      field: "role",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "role",
            editableInvitations,
            currentInvitations
          ) &&
            !!(
              row.role &&
              [
                ProgramRole.Trainee,
                ProgramRole.Attending,
                ProgramRole.ProgramAdmin
              ].some((allowedRole) => allowedRole === row.role)
            )),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "role",
          `Must be "Trainee", "Attending", or "ProgramAdmin"`,
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Trainee Type"),
      field: "traineeType",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "traineeType",
            editableInvitations,
            currentInvitations
          ) &&
            !(
              row.role === ProgramRole.Trainee &&
              ![
                TraineeType.Categorical,
                TraineeType.DesignatedPreliminary,
                TraineeType.NonDesignatedPreliminary,
                TraineeType.Fellow,
                TraineeType.MedicalStudent
              ].some((allowedType) => allowedType === row.traineeType)
            )),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "traineeType",
          row.role === ProgramRole.Trainee
            ? "Must be one of: Categorical, DesignatedPreliminary, NonDesignatedPreliminary, Fellow, MedicalStudent"
            : "(do not use for this role)",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Job Title"),
      field: "jobTitle",
      cellStyle: {
        maxWidth: "150px",
        overflow: "hidden",
        textOverflow: "ellipsis"
      },
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "jobTitle",
            editableInvitations,
            currentInvitations
          ) &&
            (row.jobTitle === undefined ||
              row.jobTitle === "" ||
              row.jobTitle.length <= 40)),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "jobTitle",
          "Optional, max 40 characters.",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Invitation Status"),
      field: "invitationStatus",
      editable: "never",
      render: (row) => {
        const { status, color } = bulkInviteRowRender(
          row,
          currentInvitations,
          validatedInvitations,
          statusMessages
        );
        return (
          <Typography color={color} fontWeight="bold">
            {t(status)}
          </Typography>
        );
      }
    }
  ];

  return (
    <MaterialTable
      tableRef={tableRef}
      columns={columns}
      data={editableInvitations}
      options={{
        showTitle: false,
        pageSize: 1000,
        pageSizeOptions: [5, 10, 20, 1000],
        emptyRowsWhenPaging: false
      }}
      isLoading={loading}
      components={{
        Toolbar: (props) => null
      }}
      actions={[
        (rowData) => {
          const hasExistingUserError = bulkInviteRowContainsExistingUserError(
            rowData,
            statusMessages
          );
          const deleteRowAction = (options?: { skipValidation?: boolean }) => {
            bulkInviteTableRowDelete({
              currentInvitations,
              targetRow: rowData,
              setInvitations,
              statusMessages,
              setStatusMessages,
              onDelete: onRowDelete,
              setRowsAwaitingRevalidation,
              skipValidationOnDelete: options?.skipValidation
            });
          };
          return {
            icon: "add",
            tooltip: "Add existing User to Program.",
            onClick: async (event, rowData) => {
              if (!Array.isArray(rowData)) {
                let usersWithNPI: User[] | null | undefined = null;
                const usersWithEmail = await getUsersByEmail(rowData.email);
                if (rowData.npi) {
                  usersWithNPI = await getUsersByNPI(rowData.npi);
                }
                // undefined = the lookup failed; don't open the modal without the user.
                if (usersWithEmail === undefined || usersWithNPI === undefined) {
                  dispatch(setToastStyle("error"));
                  dispatch(
                    setToastMessage(
                      t(
                        usersWithEmail === undefined
                          ? ExistingUserLookupErrors.EmailCheckFailed
                          : ExistingUserLookupErrors.NpiCheckFailed
                      )
                    )
                  );
                  dispatch(setShowToast(true));
                  return;
                }
                const usersAreSame = !!(
                  usersWithEmail &&
                  usersWithNPI &&
                  usersWithEmail.every(
                    (userWithEmail) =>
                      !!usersWithNPI?.find(
                        (userWithNPI) => userWithNPI.id === userWithEmail.id
                      )
                  )
                );
                const removeAwaitingRevalidation = () => {
                  if (rowData?.requestId) {
                    setRowsAwaitingRevalidation((prev) => {
                      if (!prev.has(rowData.requestId!)) return prev;
                      const updated = new Set(prev);
                      updated.delete(rowData.requestId!);
                      return updated;
                    });
                  }
                };
                // Dev Note: I left this in place since dispatch needs to be called from inside of a component.
                dispatch(
                  setModalContent(
                    <AddUserToProgram
                      existingUsersWithEmail={usersWithEmail}
                      existingUsersWithNPI={
                        usersAreSame ? undefined : usersWithNPI
                      }
                      selectedProgramRole={rowData.role}
                      selectedProgramTraineeType={
                        rowData.traineeType as TraineeType
                      }
                      selectedNPI={rowData.npi}
                      selectedInstitutionId={selectedInstitutionId}
                      selectedSimplLearnId={rowData.simplLearnId}
                      selectedIdentifiers={bulkExtraIdentifiers(rowData, {
                        skipEmpty: true
                      })}
                      identifierLabels={Object.fromEntries(
                        extraIdentifierColumns.map(({ field, title }) => [
                          identifierKeyForColumn(field),
                          title
                        ])
                      )}
                      selectedProgramId={selectedProgramId}
                      selectedYearInStage={rowData.yearInStage ?? null}
                      postSuccessAction={() => {
                        removeAwaitingRevalidation();
                        deleteRowAction({ skipValidation: true });
                        dispatch(setShowModal(false));
                      }}
                      deleteRow={() => {
                        removeAwaitingRevalidation();
                        deleteRowAction({ skipValidation: true });
                      }}
                      isBulkAddition={true}
                    />
                  )
                );
                dispatch(setShowModal(true));
              }
            },
            hidden: !hasExistingUserError,
            disabled: loading
          };
        }
      ]}
      editable={{
        isEditHidden: (rowData) =>
          bulkInviteRowContainsExistingUserError(rowData, statusMessages),
        isEditable: () => !loading,
        isDeletable: () => !loading,
        onRowAdd: (newRow) => {
          return new Promise<void>((resolve) => {
            setInvitations([...currentInvitations, newRow] as BulkInvitation[]);
            resolve();
          });
        },
        onRowUpdate: (newData, oldData) => {
          return new Promise<void>((resolve) => {
            bulkInviteTableRowUpdate({
              currentInvitations,
              oldData,
              newData,
              setInvitations,
              statusMessages,
              setStatusMessages
            });
            resolve();
          });
        },
        onRowDelete: (oldData) => {
          return new Promise<void>((resolve) => {
            bulkInviteTableRowDelete({
              currentInvitations,
              targetRow: oldData,
              setInvitations
            });
            resolve();
          });
        }
      }}
    />
  );
};
